import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    fetchGeneralSettings as fetchGeneralSettingsAPI,
    updateGeneralSettings as updateGeneralSettingsAPI,
} from '../services/generalSettingsApi';
import {
    fetchRewardSettings as fetchRewardSettingsAPI,
    updateRewardSettings as updateRewardSettingsAPI,
} from '../services/rewardsSettingsApi';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
const logger = createLogger('settingsSlice');

/** Form keys for merchant social profile URLs (general settings). */
export const SOCIAL_PLATFORM_KEYS = ['facebook_url', 'instagram_url', 'twitter_url', 'linkedin_url'];

/**
 * Which social rows should appear as "on" when there is no explicit draft toggles yet:
 * any non-empty saved URL enables that platform.
 */
export function defaultSocialPlatformsEnabledFromForm(form) {
    if (!form) {
        return {
            facebook_url: false,
            instagram_url: false,
            twitter_url: false,
            linkedin_url: false,
        };
    }
    return {
        facebook_url: Boolean(form.facebook_url?.trim()),
        instagram_url: Boolean(form.instagram_url?.trim()),
        twitter_url: Boolean(form.twitter_url?.trim()),
        linkedin_url: Boolean(form.linkedin_url?.trim()),
    };
}

export const fetchGeneralSettings = createAsyncThunk(
    'settings/fetchGeneralSettings',
    async (_, { rejectWithValue }) => {
        try {
            logger.info('Thunk: fetchGeneralSettings - started');
            const res = await fetchGeneralSettingsAPI();
            logger.info('Thunk: fetchGeneralSettings - success', { data: res.data });
            return res.data;
        } catch (error) {
            logger.error('Thunk: fetchGeneralSettings - error', { error });
            return rejectWithValue(extractErrorMessage(error));
        }
    }
);

export const updateGeneralSettings = createAsyncThunk(
    'settings/updateGeneralSettings',
    async (payload, { rejectWithValue }) => {
        try {
            logger.info('Thunk: updateGeneralSettings - started', { payload });
            const res = await updateGeneralSettingsAPI(payload);
            logger.info('Thunk: updateGeneralSettings - success', { data: res.data });
            return res.data;
        } catch (error) {
            logger.error('Thunk: updateGeneralSettings - error', { error });
            return rejectWithValue(extractErrorMessage(error));
        }
    }
);

export const fetchRewardSettings = createAsyncThunk(
    'settings/fetchRewardSettings',
    async (_, { rejectWithValue }) => {
        try {
            logger.info('Thunk: fetchRewardSettings - started');
            const res = await fetchRewardSettingsAPI();
            logger.info('Thunk: fetchRewardSettings - success', { data: res.data });
            return res.data;
        } catch (error) {
            logger.error('Thunk: fetchRewardSettings - error', { error });
            return rejectWithValue(extractErrorMessage(error));
        }
    }
);

export const updateRewardSettings = createAsyncThunk(
    'settings/updateRewardSettings',
    async (payload, { rejectWithValue }) => {
        try {
            logger.info('Thunk: updateRewardSettings - started', { payload });
            const res = await updateRewardSettingsAPI(payload);
            logger.info('Thunk: updateRewardSettings - success', { data: res.data });
            return res.data;
        } catch (error) {
            logger.error('Thunk: updateRewardSettings - error', { error });
            return rejectWithValue(extractErrorMessage(error));
        }
    }
);

/**
 * In-progress General Settings form (survives navigating to other settings tabs).
 * Cleared after a successful save or on logout (via initialState reset).
 */
export const initialGeneralSettingsFormDraft = () => ({
    form: {
        name: '',
        phone: '',
        category: '',
        address: '',
        website: '',
        review_link: '',
        facebook_url: '',
        instagram_url: '',
        twitter_url: '',
        linkedin_url: '',
        brand_color: '#6366F1',
        text_color: '#ffffff',
        secondary_color: '#11D0C3',
        logo_url: '',
    },
    socialPlatformsEnabled: {
        facebook_url: false,
        instagram_url: false,
        twitter_url: false,
        linkedin_url: false,
    },
});

export const initialState = {
    loading: false,
    error: null,
    /** Only reflects fetchGeneralSettings — not reward thunks or shared `loading` noise. */
    generalSettingsFetchStatus: 'idle',
    generalsettings: {},
    /** Unsaved General tab form; kept when switching settings sub-routes. */
    generalSettingsFormDraft: null,
    rewardsettings: {},
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setGeneralSettingsFormDraft: (state, action) => {
            const { form: formUpdate, socialPlatformsEnabled: speUpdate } = action.payload || {};
            if (!state.generalSettingsFormDraft) {
                const defaults = initialGeneralSettingsFormDraft();
                const newForm = formUpdate ? { ...defaults.form, ...formUpdate } : defaults.form;
                state.generalSettingsFormDraft = {
                    form: newForm,
                    socialPlatformsEnabled:
                        speUpdate !== undefined && speUpdate !== null
                            ? { ...defaults.socialPlatformsEnabled, ...speUpdate }
                            : defaultSocialPlatformsEnabledFromForm(newForm),
                };
            } else {
                if (formUpdate) {
                    state.generalSettingsFormDraft.form = {
                        ...state.generalSettingsFormDraft.form,
                        ...formUpdate,
                    };
                }
                if (speUpdate !== undefined && speUpdate !== null) {
                    const prev =
                        state.generalSettingsFormDraft.socialPlatformsEnabled ||
                        defaultSocialPlatformsEnabledFromForm(state.generalSettingsFormDraft.form);
                    state.generalSettingsFormDraft.socialPlatformsEnabled = { ...prev, ...speUpdate };
                }
            }
        },
        clearGeneralSettingsFormDraft: (state) => {
            state.generalSettingsFormDraft = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGeneralSettings.pending, (state) => {
                state.generalSettingsFetchStatus = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGeneralSettings.fulfilled, (state, action) => {
                state.generalSettingsFetchStatus = 'succeeded';
                state.loading = false;
                state.error = null;
                state.generalsettings = action.payload;
            })
            .addCase(fetchGeneralSettings.rejected, (state, action) => {
                state.generalSettingsFetchStatus = 'failed';
                state.loading = false;
                state.error =
                    extractErrorMessage(action.payload) ||
                    action.error?.message ||
                    'Failed to fetch general settings';
            });

        builder
            .addCase(updateGeneralSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateGeneralSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.generalsettings = action.payload;
                state.generalSettingsFormDraft = null;
            })
            .addCase(updateGeneralSettings.rejected, (state, action) => {
                state.loading = false;
                const errorMessage =
                    extractErrorMessage(action.payload) || action.error?.message;
                state.error = errorMessage || `Failed to ${updateGeneralSettings.typePrefix}`;
            });
        
        // Custom handler for fetchRewardSettings (has special payload handling)
        builder
            .addCase(fetchRewardSettings.pending, (state) => {
                logger.info('Reducer: fetchRewardSettings.pending');
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRewardSettings.fulfilled, (state, action) => {
                logger.info('Reducer: fetchRewardSettings.fulfilled', { payload: action.payload });
                state.loading = false;
                state.error = null;
                // Store the actual settings object, not { message, data }
                state.rewardsettings = action.payload.data ?? action.payload; //TODO debug that <-
            })
            .addCase(fetchRewardSettings.rejected, (state, action) => {
                logger.error('Reducer: fetchRewardSettings.rejected', { error: action.payload });
                state.loading = false;
                state.error =
                    extractErrorMessage(action.payload) ||
                    action.error?.message ||
                    'Failed to fetch reward settings';
            });
        
        // Custom handler for updateRewardSettings (has special payload handling)
        builder
            .addCase(updateRewardSettings.pending, (state) => {
                logger.info('Reducer: updateRewardSettings.pending');
                state.loading = true;
                state.error = null;
            })
            .addCase(updateRewardSettings.fulfilled, (state, action) => {
                logger.info('Reducer: updateRewardSettings.fulfilled', { payload: action.payload });
                state.loading = false;
                state.error = null;
                state.rewardsettings = action.payload.data ?? action.payload;
            })
            .addCase(updateRewardSettings.rejected, (state, action) => {
                logger.error('Reducer: updateRewardSettings.rejected', { error: action.payload });
                state.loading = false;
                state.error =
                    extractErrorMessage(action.payload) ||
                    action.error?.message ||
                    'Failed to update reward settings';
            });
    },
});

export default settingsSlice.reducer;
export const { clearError, setGeneralSettingsFormDraft, clearGeneralSettingsFormDraft } =
    settingsSlice.actions;
export const settingsSliceAPI = {
    fetchGeneralSettings,
    updateGeneralSettings,
    fetchRewardSettings,
    updateRewardSettings,
};
